import Modal from 'react-modal';
import React, { useEffect, useState } from 'react';
import { db } from '../../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { toast, ToastContainer } from 'react-toastify';
import logger from '../../../utils/logger';

const FeedbackDetails = ({ isOpen, onRequestClose, feedback, customerName }) => {
    const [vehicleDetails, setVehicleDetails] = useState({});
    const [employeeName, setEmployeeName] = useState('Loading...');
    const [isUpdating, setIsUpdating] = useState(false);
    const [managerComments, setManagerComments] = useState('');
    const [customerCommunication, setCustomerCommunication] = useState('');
    const [status, setStatus] = useState(feedback.status);

    useEffect(() => {
        const fetchVehicleDetails = async () => {
            if (feedback.vehicleId) {
                const vehicleRef = doc(db, 'vehicles', feedback.vehicleId);
                const vehicleSnap = await getDoc(vehicleRef);

                if (vehicleSnap.exists()) {
                    setVehicleDetails(vehicleSnap.data());
                } else {
                    logger.warn("No such vehicle!");
                }
            }
        };

        fetchVehicleDetails();
    }, [feedback.vehicleId]);

    useEffect(() => {
        const fetchEmployeeName = async () => {
            if (feedback.employeeId) {
                const employeeRef = doc(db, 'employees', feedback.employeeId);
                const employeeSnap = await getDoc(employeeRef);

                if (employeeSnap.exists()) {
                    const employeeData = employeeSnap.data();
                    setEmployeeName(`${employeeData.firstName || ''} ${employeeData.lastName || ''}`);
                } else {
                    setEmployeeName('Unknown');
                    logger.warn("No such employee!");
                }
            } else {
                setEmployeeName('Not Applicable');
            }
        };

        fetchEmployeeName();
    }, [feedback.employeeId]);

    const handleUpdateStatus = async (e) => {
        e.preventDefault();
        if (!feedback.id) {
            logger.error("Feedback ID is missing.");
            return;
        }

        const updateData = {
            status: status,
            managerComments: managerComments,
            customerCommunication: customerCommunication,
        };

        if (status === "Resolved") {
            updateData.resolvedDate = new Date();
        }

        try {
            const feedbackRef = doc(db, "feedbacks", feedback.id);
            await updateDoc(feedbackRef, updateData);
            toast.success("Feedback status updated successfully.");
            
        } catch (error) {
            logger.error("Error updating feedback status: ", error);
            toast.error("Failed to update feedback status.");
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className="relative bg-white rounded-2xl shadow-2xl max-w-4xl mx-auto my-8 outline-none max-h-[90vh] overflow-hidden flex flex-col"
            overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        >
            <div className="flex flex-col h-full">
                {/* Header */}
                <div className={`${
                    feedback.feedbackType === "Complaint"
                        ? "bg-gradient-to-r from-red-600 to-rose-600"
                        : "bg-gradient-to-r from-green-600 to-emerald-600"
                } px-6 py-5 flex-shrink-0`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                                {feedback.feedbackType === "Complaint" ? (
                                    <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                ) : (
                                    <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                )}
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-1">
                                    {feedback.feedbackType}
                                </h3>
                                <p className="text-white/80 text-sm font-medium">
                                    Posted: {feedback.date.toDate().toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </p>
                            </div>
                        </div>
                        <button
                            className="text-white/80 hover:text-white transition-colors duration-200"
                            onClick={onRequestClose}
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-4">
                        {/* Customer Info */}
                        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100">
                            <p className="text-xs font-semibold text-indigo-700 uppercase mb-2">Customer</p>
                            <p className="text-base font-bold text-gray-900">{customerName}</p>
                        </div>

                        {/* Vehicle Info */}
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
                            <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Vehicle Affected</p>
                            <p className="text-sm font-semibold text-gray-900">
                                {vehicleDetails?.vehicleMake} {vehicleDetails?.vehicleModel} ({vehicleDetails?.year})
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                                Registration: {vehicleDetails?.registrationNumber}
                            </p>
                        </div>

                        {/* Employee Info */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                            <p className="text-xs font-semibold text-purple-700 uppercase mb-2">Employee Affected</p>
                            <p className="text-base font-bold text-gray-900">{employeeName}</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Escalation Mode */}
                            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
                                <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">Mode of Escalation</p>
                                <p className="text-sm font-semibold text-gray-900">{feedback.escalationMode}</p>
                            </div>

                            {/* Nature */}
                            <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-xl p-4 border border-rose-100">
                                <p className="text-xs font-semibold text-rose-700 uppercase mb-2">Nature of Escalation</p>
                                <p className="text-sm font-semibold text-gray-900">{feedback.complaintNature}</p>
                            </div>
                        </div>

                        {/* Customer Comments */}
                        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-100">
                            <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Customer Comments</p>
                            <p className="text-sm text-gray-900 leading-relaxed">{feedback.feedback}</p>
                        </div>

                        {/* Initial Response */}
                        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 border border-slate-100">
                            <p className="text-xs font-semibold text-slate-700 uppercase mb-2">Initial Response to Customer</p>
                            <p className="text-sm text-gray-900 leading-relaxed">{feedback.companyFeedback}</p>
                        </div>

                        {/* Resolved Information */}
                        {feedback.status === "Resolved" && (
                            <>
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-5 h-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <p className="text-xs font-semibold text-green-700 uppercase">Manager's Comments</p>
                                    </div>
                                    <p className="text-sm text-gray-900 leading-relaxed">{feedback.managerComments}</p>
                                </div>

                                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                        </svg>
                                        <p className="text-xs font-semibold text-indigo-700 uppercase">Official Response</p>
                                    </div>
                                    <p className="text-sm text-gray-900 leading-relaxed">{feedback.customerCommunication}</p>
                                </div>

                                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
                                    <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Date Resolved</p>
                                    <p className="text-sm font-semibold text-gray-900">
                                        {feedback.resolvedDate?.toDate().toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                            </>
                        )}

                        {/* Update Form */}
                        {feedback.status !== "Resolved" && (
                            <div className="space-y-4 mt-6 pt-6 border-t border-gray-200">
                                <div>
                                    <label htmlFor="managerComments" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Manager Comments
                                    </label>
                                    <textarea
                                        id="managerComments"
                                        value={managerComments}
                                        onChange={(e) => setManagerComments(e.target.value)}
                                        rows={3}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                                        placeholder="Enter manager's comments..."
                                    />
                                </div>

                                <div>
                                    <label htmlFor="customerCommunication" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Official Response to Customer
                                    </label>
                                    <textarea
                                        id="customerCommunication"
                                        value={customerCommunication}
                                        onChange={(e) => setCustomerCommunication(e.target.value)}
                                        rows={3}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                                        placeholder="Enter official response to customer..."
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                {feedback.status !== "Resolved" && (
                    <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
                        {isUpdating ? (
                            <form onSubmit={handleUpdateStatus} className="flex items-center gap-4">
                                <div className="flex-1">
                                    <select
                                        id="status"
                                        onChange={(e) => setStatus(e.target.value)}
                                        value={status}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
                                    >
                                        <option value="">Select Status</option>
                                        <option value="Open">Open</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Resolved">Resolved</option>
                                    </select>
                                </div>
                                <button
                                    type="submit"
                                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 flex items-center"
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Submit Update
                                </button>
                            </form>
                        ) : (
                            <button
                                type="button"
                                className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center"
                                onClick={() => setIsUpdating(true)}
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Update Status
                            </button>
                        )}
                    </div>
                )}
            </div>
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </Modal>
    );
};

export default FeedbackDetails;
